import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  ButtonBase,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  InputAdornment,
  ListItemText,
  Menu,
  MenuItem,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  actionMenuMenuListSx,
  getActionMenuListContainerSx,
  getActionMenuPaperSx,
  getDashboardAccountMenuItemDangerSx,
  getDashboardAccountMenuItemSx,
} from '../../utils/actionMenuStyles';
import SearchIcon from '@mui/icons-material/Search';
import HomeWorkOutlined from '@mui/icons-material/HomeWorkOutlined';
import LinkIcon from '@mui/icons-material/Link';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMore from '@mui/icons-material/ExpandMore';
import dayjs from 'dayjs';
import Cookies from 'js-cookie';
import { FLOOR_PLAN_PREVIEW_MAX_SCALE } from '../../constants/floorPlanPreview';
import { useGetSavedLayoutByPartnerIdQuery } from '../../Slices/roomNateApis';
import { layoutLinkByUuid, resolveFloorGalleryUrl } from './layoutLinkUtils';
import {
  hasEnrichedLinkedCatalog,
  linkedCatalogFromListingLinks,
  mergeCatalogWithLinkedLayouts,
} from './linkedLayoutCatalog';
import { countUnlinkedPlansInCatalog, indexListingsSummaryByLayout, listingMetricsForScope, lookupListingSummaryForLink } from './linkedListingRollup';
import { formatListingStatPeriodLine } from './listingStatPeriodCaption';
import {
  buildFloorPlanPreviewCandidates,
  mergeLayoutPreviewFields,
} from './floorPlanPreviewUrl';
import {
  buildLinkedStatusFilterOptions,
  getLinkedStatusFilterLabel,
  LINKED_LISTING_SORT_KEYS,
  LINKED_STATUS_FILTER_ALL,
  linkedRootMatchesStatusFilter,
  sortLinkedPlanRoots,
} from './linkedListingFilters';
import { supplementCatalogFromSubmissionFeed } from './feedCatalogSupplement';
import {
  buildPartnerLayoutTree,
  filterDisplayTreeRoots,
  flattenPartnerLayoutsFromApi,
} from './layoutTreeUtils';
import SegmentedTabs from './SegmentedTabs';
import { DRILLDOWN_COLLAPSE_TRIGGER_SX } from './analyticsDrilldownStyles';
import { ANALYTICS_GREEN, ANALYTICS_RED, analyticsGreenAlpha, analyticsRedAlpha } from './analyticsColors';
import { CLIENT_COPY_LISTING_PHRASE } from './signupFloorPlanOrigin';
import { ListingStatusChip } from './ListingsSummarySection';

const PAGE_LIMIT = 30;
const MAX_AUTO_PAGES = 15;

const LINKED_STATUS_MENU_WIDTH = {
  xs: 'min(300px, calc(100vw - 16px))',
  sm: 320,
};

/** Collapsed pill fill — same as DateRangePill in AnalyticsTwo.js */
const ANALYTICS_PILL_BG = 'rgba(0,0,0,0.05)';
const ANALYTICS_PILL_BG_HOVER = 'rgba(0,0,0,0.08)';

/** Status filter pill — matches DateRangePill chrome + action menu list scroll. */
const LinkedStatusFilterPill = ({ value, options, onChange }) => {
  const theme = useTheme();
  const [anchor, setAnchor] = useState(null);
  const current = options.find((o) => o.value === value) || options[0];

  return (
    <>
      <Box
        component="button"
        type="button"
        onClick={(e) => setAnchor(e.currentTarget)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 0.75,
          width: '100%',
          pl: 1.5,
          pr: 1.25,
          py: { xs: 1, sm: 0.85 },
          minHeight: { xs: 40, sm: 36 },
          borderRadius: '10px',
          bgcolor: ANALYTICS_PILL_BG,
          border: 'none',
          color: 'rgba(0,0,0,0.88)',
          fontSize: { xs: '0.9rem', sm: '0.875rem' },
          fontWeight: 500,
          cursor: 'pointer',
          fontFamily: 'inherit',
          transition: 'background 160ms ease',
          '&:hover': { bgcolor: ANALYTICS_PILL_BG_HOVER },
        }}
      >
        <Box
          component="span"
          sx={{
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            textAlign: 'left',
          }}
        >
          {current?.label}
        </Box>
        <ExpandMore sx={{ fontSize: 18, color: 'rgba(60,60,67,0.6)', flexShrink: 0 }} />
      </Box>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              ...getActionMenuPaperSx(theme, 'dropdown'),
              mt: 1,
              p: 0.75,
              width: LINKED_STATUS_MENU_WIDTH,
              maxWidth: LINKED_STATUS_MENU_WIDTH,
              minWidth: 'unset',
            },
          },
        }}
        MenuListProps={{
          sx: {
            ...getActionMenuListContainerSx('embedded', { uniformSpacing: true }),
            ...actionMenuMenuListSx,
            p: 0,
          },
        }}
      >
        {options.map((option) => (
          <MenuItem
            key={option.value}
            selected={option.value === value}
            onClick={() => {
              onChange(option.value);
              setAnchor(null);
            }}
            sx={{
              ...getDashboardAccountMenuItemSx(theme),
              ...(option.value === value && {
                fontWeight: 600,
                color: 'rgba(0,0,0,0.88)',
                bgcolor: 'rgba(0,0,0,0.06)',
                '&.Mui-selected': {
                  bgcolor: 'rgba(0,0,0,0.06)',
                },
                '&.Mui-selected:hover': {
                  bgcolor: 'rgba(0,0,0,0.08)',
                },
              }),
            }}
          >
            {option.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

const DISPLAY_PAGE_SIZE = 5;
const THUMBNAIL_SIZE = 112;

const formatUploadDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

// Compact display name for the upload/copy author shown on each row.
// Prefer the saved name, then the local-part of the email, then nothing —
// so partner admins can always see who put a plan into the library without
// leaking full email addresses into the dense card layout.
const formatPlanAuthor = (author) => {
  if (!author) return '';
  const name = (author.name || '').trim();
  if (name) return name;
  const email = (author.email || '').trim();
  if (!email) return '';
  const local = email.split('@')[0];
  return local || email;
};

function countDescendants(node) {
  if (!node?.children?.length) return 0;
  return node.children.reduce((sum, c) => {
    if (c?.isFork && c?.parentMissing) return sum;
    return sum + 1 + countDescendants(c);
  }, 0);
}

const planWithLineageLifetime = (plan, layoutLineageLifetimeLookup) => {
  const lifetimeRollup = layoutLineageLifetimeLookup?.(plan);
  if (!lifetimeRollup) return plan;
  return { ...plan, ...lifetimeRollup };
};

const StatPill = ({ label, value, periodLine, title }) => (
  <Box sx={{ minWidth: 0, overflow: 'hidden' }} title={title || undefined}>
    <Typography
      sx={{
        fontSize: '0.6rem',
        color: 'rgba(0,0,0,0.4)',
        lineHeight: 1.2,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{
        fontSize: '0.85rem',
        fontWeight: 600,
        color: 'rgba(0,0,0,0.72)',
        lineHeight: 1.25,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
    >
      {value}
    </Typography>
    {periodLine && (
      <Typography
        sx={{
          mt: 0.15,
          fontSize: '0.6rem',
          color: 'rgba(0,0,0,0.4)',
          lineHeight: 1.2,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {periodLine}
      </Typography>
    )}
  </Box>
);

/**
 * Six period stats rendered as a responsive grid: 2 cols on xs, 3 cols on
 * sm, 6 cols on md+. Same set of pills at every breakpoint — the layout
 * adapts but the data and chrome do not, so there is no separate mobile vs
 * desktop stats experience to keep in sync.
 */
const ScopeCaption = ({ label }) => (
  <Typography
    sx={{
      mt: 0.75,
      mb: 1,
      fontSize: '0.65rem',
      color: 'rgba(0,0,0,0.4)',
      textTransform: 'uppercase',
      letterSpacing: '0.06em',
      lineHeight: 1.2,
    }}
  >
    {label}
  </Typography>
);

/** Beats global `span { font-size: 14px !important }` in index.css on chip labels. */
const METADATA_CHIP_LABEL_SX = {
  fontSize: '0.75rem !important',
  lineHeight: '1.2 !important',
  paddingLeft: '6px !important',
  paddingRight: '6px !important',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  display: 'block',
  maxWidth: '100%',
  color: 'inherit !important',
};

const METADATA_CHIP_BASE_SX = {
  height: 22,
  fontWeight: 600,
  borderRadius: '9999px',
  flexShrink: 0,
  '& .MuiChip-label': METADATA_CHIP_LABEL_SX,
};

/** Shared neutral gray for DOM + Not linked chips. */
const METADATA_NEUTRAL_CHIP_SX = {
  bgcolor: 'rgba(0,0,0,0.05)',
  color: 'rgba(0,0,0,0.52)',
};

const DAYS_ON_MARKET_CHIP_SX = {
  ...METADATA_CHIP_BASE_SX,
  ...METADATA_NEUTRAL_CHIP_SX,
};

const ROW_DIVIDER_SPACING = 2;

const rowActionsMenuItemSx = (theme) => ({
  ...getDashboardAccountMenuItemSx(theme),
  color: 'rgba(0,0,0,0.88)',
  '& .MuiListItemText-primary': {
    fontSize: '0.875rem',
    color: 'inherit',
  },
  '& .MuiListItemText-secondary': {
    color: 'rgba(0,0,0,0.55)',
  },
  '&.Mui-disabled': {
    opacity: 1,
    color: ANALYTICS_RED,
    '& .MuiListItemText-primary': {
      color: ANALYTICS_RED,
    },
    '& .MuiListItemText-secondary': {
      color: analyticsRedAlpha(0.8),
    },
  },
});

/** Dashboard chrome, but sized/positioned for the in-row overflow anchor (not full-width header). */
const rowActionsMenuPaperSx = (theme) => ({
  ...getActionMenuPaperSx(theme, 'dropdown'),
  width: 'auto',
  minWidth: 200,
  maxWidth: 'min(280px, calc(100vw - 24px))',
  mt: 0.5,
});

const formatDaysOnMarketChipLabel = (days) => {
  if (days == null) return '— DOM';
  return `${days} DOM`;
};

const STAT_ROW_SX = {
  display: 'grid',
  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
  columnGap: { xs: 1.25, sm: 1.5 },
  width: '100%',
};

const STAT_ROWS_STACK_SX = {
  display: 'flex',
  flexDirection: 'column',
  gap: { xs: 1.75, sm: 2.25 },
  width: '100%',
};

const LinkedListingMetadataChips = ({ listing, onStatusClick }) => {
  if (!listing) return null;
  return (
    <Box
      sx={{
        mt: 0.5,
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        maxWidth: '100%',
        minWidth: 0,
      }}
    >
      <Box sx={{ minWidth: 0, maxWidth: 200, flex: '0 1 auto', overflow: 'hidden' }}>
        <ListingStatusChip
          status={listing.status || 'unknown'}
          label={listing.statusLabel || 'Unknown status'}
          dense
          onClick={onStatusClick}
        />
      </Box>
      <Chip
        size="small"
        label={formatDaysOnMarketChipLabel(listing.daysOnMarket)}
        sx={DAYS_ON_MARKET_CHIP_SX}
      />
    </Box>
  );
};

const emptyPeriodStats = () => ({
  opens: 0,
  sessions: 0,
  shuffles: 0,
  shares: 0,
  downloads: 0,
  lastActivityAt: null,
});

const formatStatCount = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '0';
  return n.toLocaleString();
};

const periodLineForMetric = (periodMetrics, key, timeRangeLabel) =>
  formatListingStatPeriodLine(periodMetrics?.[key] ?? 0, timeRangeLabel);

/** Shared metric rows — inquiries/views/sessions, then shuffles/shares/downloads. */
const ListingMetricsGrid = ({ metrics, showInquiries = true }) => (
  <Box sx={STAT_ROWS_STACK_SX}>
    <Box sx={STAT_ROW_SX}>
      {showInquiries && (
        <StatPill
          label="Listing interest"
          value={formatStatCount(metrics.inquiries ?? 0)}
        />
      )}
      <StatPill label="Views" value={formatStatCount(metrics.planOpens)} />
      <StatPill label="Sessions" value={formatStatCount(metrics.sessions)} />
    </Box>
    <Box sx={STAT_ROW_SX}>
      <StatPill label="Shuffles" value={formatStatCount(metrics.shuffles)} />
      <StatPill label="Shares" value={formatStatCount(metrics.shares)} />
      <StatPill label="Downloads" value={formatStatCount(metrics.downloads)} />
    </Box>
  </Box>
);

/** Lifetime totals with selected-period counts as muted subtitles. */
const CombinedListingMetricsGrid = ({
  lifetimeMetrics,
  periodMetrics,
  timeRangeLabel,
  showInquiries = true,
}) => (
  <Box sx={STAT_ROWS_STACK_SX}>
    <Box sx={STAT_ROW_SX}>
      {showInquiries && (
        <StatPill
          label="Listing interest"
          value={formatStatCount(lifetimeMetrics.inquiries ?? 0)}
          periodLine={periodLineForMetric(periodMetrics, 'inquiries', timeRangeLabel)}
        />
      )}
      <StatPill
        label="Views"
        value={formatStatCount(lifetimeMetrics.planOpens)}
        periodLine={periodLineForMetric(periodMetrics, 'planOpens', timeRangeLabel)}
      />
      <StatPill
        label="Sessions"
        value={formatStatCount(lifetimeMetrics.sessions)}
        periodLine={periodLineForMetric(periodMetrics, 'sessions', timeRangeLabel)}
      />
    </Box>
    <Box sx={STAT_ROW_SX}>
      <StatPill
        label="Shuffles"
        value={formatStatCount(lifetimeMetrics.shuffles)}
        periodLine={periodLineForMetric(periodMetrics, 'shuffles', timeRangeLabel)}
      />
      <StatPill
        label="Shares"
        value={formatStatCount(lifetimeMetrics.shares)}
        periodLine={periodLineForMetric(periodMetrics, 'shares', timeRangeLabel)}
      />
      <StatPill
        label="Downloads"
        value={formatStatCount(lifetimeMetrics.downloads)}
        periodLine={periodLineForMetric(periodMetrics, 'downloads', timeRangeLabel)}
      />
    </Box>
  </Box>
);

const listingMetricsFromApi = (listing, scope = 'period') => {
  const m = listingMetricsForScope(listing, scope);
  if (!m) return null;
  return {
    inquiries: m.inquiryCount,
    planOpens: m.galleryViews,
    sessions: m.sessions,
    shuffles: m.shuffles,
    shares: m.shares,
    downloads: m.downloads,
  };
};

const listingMetricsFromFeed = (periodStats) => {
  const stats = periodStats || emptyPeriodStats();
  return {
    inquiries: 0,
    planOpens: stats.opens ?? 0,
    sessions: stats.sessions ?? 0,
    shuffles: stats.shuffles ?? 0,
    shares: stats.shares ?? 0,
    downloads: stats.downloads ?? 0,
  };
};

/** Linked rows: lifetime totals with selected-period counts as subtitles. */
const LinkedPlanStatsPanel = ({
  lifetimeListing,
  periodListing,
  periodStats,
  scopeLabel,
}) => {
  const lifetimeMetrics = lifetimeListing
    ? listingMetricsFromApi(lifetimeListing, 'lifetime')
    : null;
  const periodMetrics = periodListing
    ? listingMetricsFromApi(periodListing, 'period')
    : listingMetricsFromFeed(periodStats);

  if (!lifetimeMetrics) {
    return (
      <Box sx={{ mt: 0.75, width: '100%', pt: 0.75 }}>
        {scopeLabel && <ScopeCaption label={scopeLabel} />}
        <ListingMetricsGrid metrics={periodMetrics} showInquiries />
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 0.75, width: '100%', pt: 0.75 }}>
      <CombinedListingMetricsGrid
        lifetimeMetrics={lifetimeMetrics}
        periodMetrics={periodMetrics}
        timeRangeLabel={scopeLabel}
        showInquiries
      />
    </Box>
  );
};

const FloorPlanStatsRow = ({ periodStats, scopeLabel }) => (
  <Box sx={{ mt: 0.75, width: '100%', pt: 0.75 }}>
    {scopeLabel && <ScopeCaption label={scopeLabel} />}
    <ListingMetricsGrid
      metrics={listingMetricsFromFeed(periodStats)}
      showInquiries
    />
  </Box>
);

/**
 * Tappable plan thumbnail. When `onOpen` is supplied the whole tile acts as a
 * "open this plan" button — matches the iOS pattern of treating cell artwork
 * as the primary navigation affordance (Files, Photos, App Store), and lets
 * us delete the redundant "Open" text button from each row. Rendered as a
 * `<div role="button">` (via ButtonBase `component="div"`) so it can sit
 * inside an outer row-level ButtonBase without producing invalid nested
 * `<button>` HTML.
 */
const FloorPlanThumbnail = ({ plan, onOpen, size = THUMBNAIL_SIZE }) => {
  const previewKey = [
    plan?.furnishedThumbnail,
    plan?.thumbnail,
    plan?.blankImage,
  ].join('\0');
  const candidates = useMemo(
    () => buildFloorPlanPreviewCandidates(plan),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on resolved preview URLs only
    [previewKey]
  );
  const [candidateIndex, setCandidateIndex] = useState(0);

  useEffect(() => {
    setCandidateIndex(0);
  }, [previewKey]);

  const src = candidates[candidateIndex] || null;

  const handleImgError = () => {
    setCandidateIndex((current) =>
      current + 1 < candidates.length ? current + 1 : candidates.length
    );
  };
  const openUrl = typeof onOpen === 'function' ? resolveFloorGalleryUrl(plan) : null;
  const interactive = Boolean(openUrl);
  const handleClick = (e) => {
    if (!interactive) return;
    e.stopPropagation();
    onOpen(plan);
  };

  const wrapperSx = {
    width: size,
    height: size,
    flexShrink: 0,
    bgcolor: '#F5F5F7',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    position: 'relative',
    ...(interactive && {
      textTransform: 'none',
      transition: 'background-color 160ms ease',
      '&:hover': { bgcolor: '#ECECEF' },
      '&:hover .floor-plan-thumb-open': {
        opacity: 1,
        transform: 'translateY(0)',
      },
      '&:focus-visible': {
        outline: '2px solid rgba(0,102,255,0.5)',
        outlineOffset: '2px',
      },
    }),
  };

  // CSS-only adaptive visibility for the "open" overlay badge: on devices
  // that cannot hover (touch), it is always visible so the affordance is
  // immediately discoverable. On devices that can hover (mouse/trackpad),
  // it fades in on hover via the `&:hover .floor-plan-thumb-open` rule
  // above. No JS branching — same component everywhere.
  const overlaySx = {
    position: 'absolute',
    top: 4,
    right: 4,
    width: 22,
    height: 22,
    borderRadius: '50%',
    bgcolor: 'rgba(255,255,255,0.92)',
    boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    opacity: 0,
    transform: 'translateY(-2px)',
    transition: 'opacity 160ms ease, transform 160ms ease',
    pointerEvents: 'none',
    '@media (hover: none)': {
      opacity: 1,
      transform: 'translateY(0)',
    },
  };

  const content = (
    <>
      {src ? (
        <Box
          component="img"
          key={src}
          src={src}
          alt={plan?.name ? `${plan.name} preview` : 'Floor plan preview'}
          decoding="async"
          onError={handleImgError}
          sx={{
            maxWidth: FLOOR_PLAN_PREVIEW_MAX_SCALE,
            maxHeight: FLOOR_PLAN_PREVIEW_MAX_SCALE,
            width: 'auto',
            height: 'auto',
            objectFit: 'contain',
            display: 'block',
            boxShadow: '0 1px 3px rgba(0,0,0,0.28), 0 0 1px rgba(0,0,0,0.2)',
            borderRadius: '2px',
          }}
        />
      ) : (
        <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: 'rgba(0,0,0,0.35)' }}>
          {plan?.name?.[0] || 'F'}
        </Typography>
      )}
      {interactive && (
        <Box className="floor-plan-thumb-open" aria-hidden="true" sx={overlaySx}>
          <OpenInNewIcon sx={{ fontSize: 13, color: 'rgba(0,0,0,0.6)' }} />
        </Box>
      )}
    </>
  );

  if (!interactive) {
    return <Box sx={wrapperSx}>{content}</Box>;
  }

  return (
    <ButtonBase
      component="div"
      role="button"
      tabIndex={0}
      onClick={handleClick}
      aria-label={`Open ${plan?.name || 'floor plan'}`}
      sx={wrapperSx}
    >
      {content}
    </ButtonBase>
  );
};

/**
 * iOS-style overflow menu (•••) used on mobile to collapse the row's
 * secondary actions (Change / Unlink / Open in new tab) into a single
 * 44pt touch target. On sm+ the parent renders the existing inline button
 * stack instead — this menu is mobile-only.
 */
const RowActionsMenu = ({
  plan,
  linkedRef,
  linkId,
  isLinked,
  onOpen,
  onAttach,
  onEditStatus,
  onDisconnect,
  size = 'medium',
}) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const close = () => setAnchorEl(null);
  const handleOpen = (e) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };

  const dim = size === 'small' ? 36 : 36;
  const linkDisabled = !isLinked && plan?.isFork;
  const menuItemSx = rowActionsMenuItemSx(theme);

  return (
    <>
      <IconButton
        size="small"
        aria-label="More actions"
        onClick={handleOpen}
        sx={{
          width: dim,
          height: dim,
          flexShrink: 0,
          color: '#545454',
          bgcolor: 'rgba(255,255,255,0.9)',
          border: '1px solid rgba(0,0,0,0.08)',
          boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
          '&:hover': {
            bgcolor: 'rgba(248,248,250,1)',
            borderColor: 'rgba(0,0,0,0.12)',
          },
        }}
      >
        <MoreHorizIcon sx={{ fontSize: 22 }} />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={close}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        marginThreshold={12}
        PopoverProps={{
          disableScrollLock: true,
          modifiers: [
            {
              name: 'preventOverflow',
              enabled: true,
              options: {
                boundary: 'viewport',
                padding: 12,
                altAxis: true,
              },
            },
            {
              name: 'flip',
              enabled: true,
              options: {
                padding: 12,
              },
            },
          ],
        }}
        PaperProps={{
          sx: rowActionsMenuPaperSx(theme),
        }}
        MenuListProps={{
          sx: {
            ...actionMenuMenuListSx,
            maxHeight: 'min(60vh, calc(100dvh - 96px))',
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch',
          },
        }}
      >
        {onOpen && resolveFloorGalleryUrl(plan) && (
          <MenuItem
            onClick={() => {
              close();
              onOpen(plan);
            }}
            sx={menuItemSx}
          >
            <ListItemText primary="Open" />
          </MenuItem>
        )}
        {isLinked ? (
          <MenuItem
            onClick={() => {
              close();
              onEditStatus?.(plan, linkedRef);
            }}
            sx={menuItemSx}
          >
            <ListItemText primary="Manage listing" />
          </MenuItem>
        ) : (
          <MenuItem
            disabled={linkDisabled}
            onClick={() => {
              close();
              onAttach?.(plan);
            }}
            sx={menuItemSx}
          >
            <ListItemText
              primary="Link to listing"
              secondary={
                linkDisabled ? CLIENT_COPY_LISTING_PHRASE : undefined
              }
            />
          </MenuItem>
        )}
        {isLinked && linkId && (
          <MenuItem
            onClick={() => {
              close();
              onDisconnect?.({ linkId, listingRef: linkedRef, plan });
            }}
            sx={{
              ...menuItemSx,
              ...getDashboardAccountMenuItemDangerSx(theme),
            }}
          >
            <ListItemText primary="Unlink listing" />
          </MenuItem>
        )}
      </Menu>
    </>
  );
};

const UNLINKED_CHIP_SX = {
  ...METADATA_CHIP_BASE_SX,
  ...METADATA_NEUTRAL_CHIP_SX,
};

const truncateLinkedListingRef = (linkedRef, maxLen = 14) => {
  const ref = String(linkedRef || '').trim();
  if (!ref) return '';
  if (ref.length <= maxLen) return ref;
  return `${ref.slice(0, maxLen)}…`;
};

const ListingLinkStatus = ({ isLinked, linkedRef }) => {
  if (!isLinked) {
    return (
      <Chip
        size="small"
        label="Not linked"
        sx={{ mt: 0.5, display: 'inline-flex', ...UNLINKED_CHIP_SX }}
      />
    );
  }

  const fullRef = String(linkedRef || '').trim();
  const displayRef = truncateLinkedListingRef(fullRef);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.35,
        mt: 0.5,
        minWidth: 0,
        maxWidth: '100%',
      }}
      title={fullRef || undefined}
    >
      <LinkIcon sx={{ fontSize: 13, color: ANALYTICS_GREEN, flexShrink: 0 }} />
      <Typography
        noWrap
        sx={{
          fontSize: '0.72rem',
          fontWeight: 500,
          color: ANALYTICS_GREEN,
          lineHeight: 1.35,
          minWidth: 0,
        }}
      >
        {displayRef}
      </Typography>
    </Box>
  );
};

/**
 * Single row of plan metadata + actions. One component, one interaction
 * model at every screen size — visual density adapts via `sx` breakpoints,
 * but the behavior never forks on a `useMediaQuery` flag.
 *
 * Interaction model (universal):
 *   - Thumbnail is the tap-to-open affordance (the only "open this plan"
 *     control on the row). An overlay icon reveals on hover and is always
 *     visible on touch devices, courtesy of the @media (hover) rule.
 *   - Secondary actions (Open in new tab / Change listing / Unlink) live
 *     in a single overflow menu (•••) when a plan is linked. When it isn't,
 *     a single inline "Link" button is the only accessory.
 *   - Parent rows surface a full-width "Show N copies" footer button that
 *     toggles expansion. The row body itself is never tappable to expand,
 *     which is why there is no left-side chevron either — one affordance,
 *     no competing gestures.
 */
const FloorPlanCard = ({
  plan,
  linkRecord,
  childCount = 0,
  showExpand = false,
  expanded = false,
  onToggleExpand,
  onAttach,
  onChange,
  onDisconnect,
  onEditListingStatus,
  onListingLinkSuccess,
  partner,
  existingLinks,
  planByCustomerRef,
  onOpen,
  periodStats = null,
  scopeLabel = null,
  lifetimeListing = null,
  periodListing = null,
}) => {
  const linkedRef = linkRecord?.customer_ref;
  const linkId = linkRecord?._id || linkRecord?.id;
  const isLinked = Boolean(linkedRef);
  const hasChildren = showExpand && childCount > 0;
  const copiesLabel = !isLinked
    ? `${childCount} client ${childCount === 1 ? 'copy' : 'copies'}`
    : `${childCount} ${childCount === 1 ? 'copy' : 'copies'}`;

  const listingForStatus = lifetimeListing || periodListing;
  const openStatusSetup = () => {
    if (!linkId || !onEditListingStatus) return;
    onEditListingStatus({
      linkId,
      listingRef: linkedRef,
      planName: plan?.name,
      mlsStatus: listingForStatus?.mlsStatus,
      customPropertyStatus: listingForStatus?.customPropertyStatus,
      onMarketDate: listingForStatus?.onMarketDate,
      onMarketDateSource: listingForStatus?.onMarketDateSource,
      offMarketDate: listingForStatus?.offMarketDate,
      mlsSyncEnabled: listingForStatus?.mlsSyncEnabled,
    });
  };

  return (
    <Box sx={{ minWidth: 0 }}>
      <Stack
        direction="row"
        alignItems="flex-start"
        spacing={1.25}
        sx={{ minWidth: 0 }}
      >
        <FloorPlanThumbnail
          plan={plan}
          onOpen={onOpen}
          size={{ xs: 92, sm: THUMBNAIL_SIZE }}
        />

        <Box sx={{ flex: 1, minWidth: 0 }}>
          {isLinked && listingForStatus && (
            <LinkedListingMetadataChips
              listing={listingForStatus}
              onStatusClick={openStatusSetup}
            />
          )}
          <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600 }} noWrap>
            {plan.name}
          </Typography>
          <Typography sx={{ fontSize: '0.7rem', color: 'rgba(0,0,0,0.45)', lineHeight: 1.45 }}>
            {plan.isFork
              ? `Copied on ${formatUploadDate(plan.createdAt)}`
              : `Uploaded ${formatUploadDate(plan.createdAt)}`}
            {(() => {
              const who = formatPlanAuthor(plan.author);
              return who ? ` by ${who}` : '';
            })()}
          </Typography>
          <ListingLinkStatus isLinked={isLinked} linkedRef={linkedRef} />
        </Box>

        {/* Single accessory: inline "Link" CTA when unlinked, overflow menu
            otherwise. Same on every screen size. */}
        <Box sx={{ flexShrink: 0, pt: 0.25 }} onClick={(e) => e.stopPropagation()}>
          {!isLinked ? (
            <Button
              size="small"
              variant="outlined"
              startIcon={<LinkIcon sx={{ fontSize: 16 }} />}
              onClick={() => onAttach?.(plan)}
              disabled={plan.isFork}
              title={plan.isFork ? CLIENT_COPY_LISTING_PHRASE : undefined}
              sx={{
                textTransform: 'none',
                fontSize: { xs: '0.78rem', sm: '0.72rem' },
                borderRadius: '10px',
                minHeight: 36,
                px: 1.25,
              }}
            >
              Link
            </Button>
          ) : (
            <RowActionsMenu
              plan={plan}
              linkedRef={linkedRef}
              linkId={linkId}
              isLinked={isLinked}
              onOpen={onOpen}
              onAttach={onAttach}
              onEditStatus={openStatusSetup}
              onDisconnect={onDisconnect}
            />
          )}
        </Box>
      </Stack>

      <Box sx={{ width: '100%' }} onClick={(e) => e.stopPropagation()}>
        {lifetimeListing || periodListing ? (
          <LinkedPlanStatsPanel
            lifetimeListing={lifetimeListing}
            periodListing={periodListing}
            periodStats={periodStats}
            scopeLabel={scopeLabel}
          />
        ) : (
          <FloorPlanStatsRow periodStats={periodStats} scopeLabel={scopeLabel} />
        )}
      </Box>

      {/*
        "Show N copies" disclosure: a full-width footer button that toggles
        expansion. Mirrors the "Load more" affordance at the bottom of the
        section. Shown on every screen size — never auto-expanded — so the
        section never starts as a wall of nested rows. iOS-style 44pt
        touch target carries over cleanly to desktop click targets.
      */}
      {hasChildren && (
        <ButtonBase
          onClick={onToggleExpand}
          aria-expanded={expanded}
          sx={{
            ...DRILLDOWN_COLLAPSE_TRIGGER_SX,
            mt: 1,
            minHeight: 44,
          }}
        >
          <Typography
            sx={{
              fontSize: '0.85rem',
              fontWeight: 500,
              color: 'rgba(0,0,0,0.75)',
            }}
          >
            {expanded ? `Hide ${copiesLabel}` : `Show ${copiesLabel}`}
          </Typography>
          <ChevronRightIcon
            sx={{
              fontSize: 18,
              color: 'rgba(0,0,0,0.4)',
              transform: expanded ? 'rotate(-90deg)' : 'rotate(90deg)',
              transition: 'transform 180ms ease',
            }}
          />
        </ButtonBase>
      )}
    </Box>
  );
};

/**
 * Compact child row — styled after the Recent Activity expanded timeline:
 * a left rail with a dot, a small thumbnail, and a single-line meta line.
 * Used for every non-root layout in nested view so client copies read as
 * subordinate to their parent without repeating the full card chrome.
 *
 * Mobile collapses secondary actions into the shared `RowActionsMenu`; the
 * redundant "Open" button is removed everywhere because the thumbnail
 * itself is now the canonical open-plan affordance.
 */
const NestedChildRow = ({
  plan,
  linkRecord,
  periodStats,
  onOpen,
  partner,
  existingLinks,
  planByCustomerRef,
  onListingLinkSuccess,
  onAttach,
  onChange,
  onDisconnect,
  scopeLabel = null,
}) => {
  const linkedRef = linkRecord?.customer_ref;
  const linkId = linkRecord?._id || linkRecord?.id;
  const isLinked = Boolean(linkedRef);
  // Skip the trailing accessory entirely when there is no useful action:
  // unlinked forks cannot be linked (handled at the parent), so showing a
  // disabled-only menu would be noise.
  const showAccessory = isLinked || !plan.isFork;

  return (
    <Box sx={{ position: 'relative', py: 1, pl: 2.5, pr: 0 }}>
      <Box
        aria-hidden="true"
        sx={{
          position: 'absolute',
          left: 7,
          top: 14,
          width: 9,
          height: 9,
          borderRadius: '50%',
          bgcolor: '#fff',
          border: '1.5px solid rgba(0,0,0,0.25)',
          zIndex: 1,
        }}
      />
      <Stack direction="row" alignItems="flex-start" spacing={1.25}>
        <FloorPlanThumbnail plan={plan} onOpen={onOpen} size={56} />

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.75}
            sx={{ flexWrap: 'wrap', rowGap: 0.25 }}
          >
            <Typography
              sx={{
                fontSize: '0.8125rem',
                fontWeight: 600,
                color: 'rgba(0,0,0,0.82)',
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '100%',
                flex: 1,
              }}
            >
              {plan.name}
            </Typography>
            {showAccessory && (
              <Box onClick={(e) => e.stopPropagation()}>
                {isLinked ? (
                  <RowActionsMenu
                    plan={plan}
                    linkedRef={linkedRef}
                    linkId={linkId}
                    isLinked={isLinked}
                    onOpen={onOpen}
                    onAttach={onAttach}
                    onDisconnect={onDisconnect}
                    size="small"
                  />
                ) : (
                  <Button
                    size="small"
                    variant="text"
                    startIcon={<LinkIcon sx={{ fontSize: 14 }} />}
                    onClick={() => onAttach?.(plan)}
                    sx={{
                      textTransform: 'none',
                      fontSize: '0.72rem',
                      minWidth: 0,
                      px: 0.75,
                      minHeight: 32,
                    }}
                  >
                    Link
                  </Button>
                )}
              </Box>
            )}
          </Stack>
          <Typography sx={{ fontSize: '0.7rem', color: 'rgba(0,0,0,0.45)', mt: 0.25, lineHeight: 1.4 }}>
            Copied on {formatUploadDate(plan.createdAt)}
            {(() => {
              const who = formatPlanAuthor(plan.author);
              return who ? ` by ${who}` : '';
            })()}
          </Typography>
          {isLinked && (
            <ListingLinkStatus isLinked={isLinked} linkedRef={linkedRef} />
          )}
        </Box>
      </Stack>

      <Box sx={{ width: '100%', pl: 0 }} onClick={(e) => e.stopPropagation()}>
        <FloorPlanStatsRow periodStats={periodStats} scopeLabel={scopeLabel} />
      </Box>
    </Box>
  );
};

/**
 * Recursive tree node. Roots render as the full `FloorPlanCard`. Children
 * render via `NestedChildRow` and sit inside a shared rail container with a
 * vertical timeline line that visually ties siblings to the parent — same
 * pattern as the Recent Activity expanded session view.
 */
const FloorPlanTreeNode = ({
  node,
  depth = 0,
  linksByUuid,
  autoExpandedIds,
  defaultExpandedIds,
  layoutStatsLookup,
  layoutLineagePeriodLookup = null,
  layoutLineageLifetimeLookup = null,
  listingsLifetimeByKey = null,
  listingsPeriodByKey = null,
  onAttach,
  onChange,
  onDisconnect,
  onListingLinkSuccess,
  onEditListingStatus,
  partner,
  existingLinks,
  planByCustomerRef,
  onOpen,
  scopeLabel = null,
}) => {
  const [userExpanded, setUserExpanded] = useState(null);
  const children = node.children || [];
  const hasChildren = children.length > 0;
  const autoExpanded = autoExpandedIds?.has?.(node._id) || false;
  const defaultExpanded = defaultExpandedIds?.has?.(node._id) || false;
  const expanded =
    userExpanded === null ? autoExpanded || defaultExpanded : userExpanded;

  const linkRecord = node.uuid ? linksByUuid?.get?.(node.uuid) : null;
  const linkedListingLifetime = lookupListingSummaryForLink(listingsLifetimeByKey, linkRecord);
  const linkedListingPeriod = lookupListingSummaryForLink(listingsPeriodByKey, linkRecord);
  const total = countDescendants(node);
  const isRootRow = depth === 0;
  const periodStats = isRootRow
    ? layoutLineagePeriodLookup?.(node) || layoutStatsLookup?.(node) || null
    : layoutStatsLookup?.(node) || null;
  const planForStats = isRootRow
    ? planWithLineageLifetime(node, layoutLineageLifetimeLookup)
    : node;

  if (depth > 0) {
    return (
      <>
        <NestedChildRow
          plan={node}
          linkRecord={linkRecord}
          periodStats={periodStats}
          onOpen={onOpen}
          partner={partner}
          existingLinks={existingLinks}
          planByCustomerRef={planByCustomerRef}
          onListingLinkSuccess={onListingLinkSuccess}
          onAttach={onAttach}
          onChange={onChange}
          onDisconnect={onDisconnect}
          scopeLabel={scopeLabel}
        />
        {hasChildren &&
          children.map((child, childIndex) => (
            <Box
              key={child._id}
              sx={{
                borderTop:
                  childIndex > 0 ? '1px solid rgba(0,0,0,0.06)' : 'none',
              }}
            >
            <FloorPlanTreeNode
              node={child}
              depth={depth + 1}
              linksByUuid={linksByUuid}
              autoExpandedIds={autoExpandedIds}
              defaultExpandedIds={defaultExpandedIds}
              layoutStatsLookup={layoutStatsLookup}
              layoutLineagePeriodLookup={layoutLineagePeriodLookup}
              layoutLineageLifetimeLookup={layoutLineageLifetimeLookup}
              listingsLifetimeByKey={listingsLifetimeByKey}
              listingsPeriodByKey={listingsPeriodByKey}
              onAttach={onAttach}
              onChange={onChange}
              onDisconnect={onDisconnect}
              onEditListingStatus={onEditListingStatus}
              onListingLinkSuccess={onListingLinkSuccess}
              partner={partner}
              existingLinks={existingLinks}
              planByCustomerRef={planByCustomerRef}
              onOpen={onOpen}
              scopeLabel={scopeLabel}
            />
            </Box>
          ))}
      </>
    );
  }

  return (
    <Box
      sx={{
        borderTop: '1px solid rgba(0,0,0,0.06)',
        '&:first-of-type': { borderTop: 'none', pt: 0 },
        pt: ROW_DIVIDER_SPACING,
        pb: ROW_DIVIDER_SPACING,
      }}
    >
      <Box>
        <FloorPlanCard
          plan={planForStats}
          linkRecord={linkRecord}
          childCount={total}
          showExpand={hasChildren}
          expanded={expanded}
          onToggleExpand={() => setUserExpanded((v) => !(v ?? expanded))}
          onAttach={onAttach}
          onChange={onChange}
          onDisconnect={onDisconnect}
          onEditListingStatus={onEditListingStatus}
          onListingLinkSuccess={onListingLinkSuccess}
          partner={partner}
          existingLinks={existingLinks}
          planByCustomerRef={planByCustomerRef}
          onOpen={onOpen}
          periodStats={periodStats}
          scopeLabel={scopeLabel}
          lifetimeListing={linkedListingLifetime}
          periodListing={linkedListingPeriod}
        />
      </Box>
      {hasChildren && (
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Box
            sx={{
              position: 'relative',
              ml: 4,
              pl: 0,
              pb: 1,
              '&::before': {
                content: '""',
                position: 'absolute',
                left: 11,
                top: 0,
                bottom: 12,
                width: '1px',
                bgcolor: 'rgba(0,0,0,0.1)',
              },
            }}
          >
            {children.map((child, childIndex) => (
              <Box
                key={child._id}
                sx={{
                  borderTop:
                    childIndex > 0 ? '1px solid rgba(0,0,0,0.06)' : 'none',
                  pt: childIndex > 0 ? ROW_DIVIDER_SPACING : 0,
                  pb: ROW_DIVIDER_SPACING,
                }}
              >
              <FloorPlanTreeNode
                node={child}
                depth={1}
                linksByUuid={linksByUuid}
                autoExpandedIds={autoExpandedIds}
                defaultExpandedIds={defaultExpandedIds}
                layoutStatsLookup={layoutStatsLookup}
                layoutLineagePeriodLookup={layoutLineagePeriodLookup}
                layoutLineageLifetimeLookup={layoutLineageLifetimeLookup}
                listingsLifetimeByKey={listingsLifetimeByKey}
              listingsPeriodByKey={listingsPeriodByKey}
                onAttach={onAttach}
                onChange={onChange}
                onDisconnect={onDisconnect}
                onEditListingStatus={onEditListingStatus}
                onListingLinkSuccess={onListingLinkSuccess}
                partner={partner}
                existingLinks={existingLinks}
                planByCustomerRef={planByCustomerRef}
                onOpen={onOpen}
                scopeLabel={scopeLabel}
              />
              </Box>
            ))}
          </Box>
        </Collapse>
      )}
    </Box>
  );
};

/**
 * Partner-admin floor plan catalog: thumbnail, title, date, attach listing.
 * Data: GET /savedLayout/getByPartner (flat list; tree built client-side).
 *
 * `onLinkByUrl` opens the partner-admin "Link by gallery URL" escape hatch
 * — required for plans uploaded outside the selected date window which would
 * otherwise be unreachable from this section.
 */
const FloorPlanLibrarySection = ({
  partnerHostname,
  partnerId: partnerIdProp = null,
  timeRange = '7d',
  timeRangeLabel = null,
  listingLinks = [],
  planByCustomerRef = null,
  // New props for the merged Floor Plans / Listings view (task 10). All
  // are optional so existing callers continue to render correctly.
  listingsSummaryPeriod = null,
  listingsSummaryLifetime = null,
  linkedStatusFilter = LINKED_STATUS_FILTER_ALL,
  onLinkedStatusFilterChange,
  partner = null,
  existingLinks = null,
  onAttachPlan,
  onChangePlan,
  onDisconnect,
  onLinkByUrl,
  onListingLinkSuccess,
  onEditListingStatus,
  linkNoticeMessage = null,
  linkNoticeIsError = false,
  onDismissLinkNotice,
  onCatalogLoaded,
  layoutStatsLookup = null,
  layoutLineagePeriodLookup = null,
  layoutLineageLifetimeLookup = null,
  sectionId = 'floor-plan-library',
  /** Bumped by Analytics Sync so the catalog refetches with a fresh page-1 window. */
  catalogRefreshKey = 0,
  /** Full-window floorplan_uploaded feed rows — fills gaps when getByPartner omits uploads. */
  submissionFeedRows = [],
}) => {
  const partnerId = partnerIdProp || Cookies.get('partner') || null;
  const [page, setPage] = useState(1);
  const [accumulated, setAccumulated] = useState([]);
  const [search, setSearch] = useState('');
  // Default to 'linked' if there's at least one linked listing — that's the
  // primary review surface once a partner has done their initial linking.
  // Falls back to 'all' for fresh partners so the empty state isn't
  // misleading.
  const [filter, setFilter] = useState(() =>
    Array.isArray(listingLinks) && listingLinks.length > 0 ? 'linked' : 'unlinked'
  );
  const linkedStatusFilterOptions = useMemo(() => buildLinkedStatusFilterOptions(), []);
  const filterInitializedRef = useRef(false);

  // Deep link: #floor-plan-library?filter=unlinked (also reacts to hashchange
  // so summary-band chips can jump here without a full reload).
  useEffect(() => {
    const applyHashFilter = () => {
      const raw = window.location.hash.replace(/^#/, '');
      if (!raw.startsWith('floor-plan-library')) return;
      const query = raw.includes('?') ? raw.split('?')[1] : '';
      const params = new URLSearchParams(query);
      const f = params.get('filter');
      if (f === 'unlinked' || f === 'linked' || f === 'all') {
        setFilter(f);
      }
      window.history.replaceState(
        null,
        '',
        window.location.pathname + window.location.search
      );
    };
    applyHashFilter();
    window.addEventListener('hashchange', applyHashFilter);
    return () => window.removeEventListener('hashchange', applyHashFilter);
  }, []);
  const [visibleCount, setVisibleCount] = useState(DISPLAY_PAGE_SIZE);
  // When a filter tab's API pagination stops yielding new matches, hide
  // "Load more" even if the global catalog still has more pages (e.g. Linked
  // tab with all linked plans visible but unlinked plans on later pages).
  const prevFilterRef = useRef(filter);
  // Sticky session memory of every layout the API has ever returned for the
  // active scope. Lets the tree keep rendering a previously-nested child
  // even if a later refetch (e.g. RTK Query invalidation after a fork)
  // returns a response that omits it. Bounded by timeRange/partnerId resets
  // below so it never grows beyond the current scope's working set.
  const [seenLayoutsFlat, setSeenLayoutsFlat] = useState(() => new Map());

  const { startDate, endDate } = useMemo(() => {
    const end = dayjs();
    if (timeRange === '24h') return { startDate: end.subtract(1, 'day'), endDate: end };
    if (timeRange === '30d') return { startDate: end.subtract(30, 'day'), endDate: end };
    return { startDate: end.subtract(7, 'day'), endDate: end };
  }, [timeRange]);

  useEffect(() => {
    setPage(1);
    setAccumulated([]);
    setSeenLayoutsFlat(new Map());
    setVisibleCount(DISPLAY_PAGE_SIZE);
  }, [timeRange, partnerId, catalogRefreshKey]);

  useEffect(() => {
    setVisibleCount(DISPLAY_PAGE_SIZE);
  }, [search, filter, linkedStatusFilter]);

  // Entering Not Linked resumes getByPartner from page 1 so new uploads on the
  // first API page are not skipped when pagination state was left on page > 1.
  useEffect(() => {
    const prev = prevFilterRef.current;
    prevFilterRef.current = filter;
    if (filter !== 'unlinked' || prev === 'unlinked') return;
    setPage(1);
    setAccumulated([]);
    setSeenLayoutsFlat(new Map());
    setVisibleCount(DISPLAY_PAGE_SIZE);
  }, [filter]);

  // Promote 'all' → 'linked' the first time listing links become available
  // (the section may mount before the listingLinks fetch resolves).
  useEffect(() => {
    if (filterInitializedRef.current) return;
    if (!Array.isArray(listingLinks) || listingLinks.length === 0) return;
    filterInitializedRef.current = true;
    setFilter('linked');
  }, [listingLinks]);

  useEffect(() => {
    if (!linkNoticeMessage) return;
    const isLinkSuccess =
      !linkNoticeIsError &&
      (linkNoticeMessage.startsWith('Linked') ||
        linkNoticeMessage === 'Floor plan linked to listing.');
    if (isLinkSuccess) {
      setFilter('linked');
    }
    document.getElementById(sectionId)?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
    });
  }, [linkNoticeMessage, linkNoticeIsError, sectionId]);

  const linkedCatalogRows = useMemo(
    () => linkedCatalogFromListingLinks(listingLinks),
    [listingLinks]
  );
  const useLinkedCatalog =
    filter === 'linked' && hasEnrichedLinkedCatalog(listingLinks);

  // Linked-tab layout snapshots can ship without loadable preview URLs. Prefetch
  // page 1 of getByPartner (no date filter) so thumbnails hydrate without a
  // full page refresh.
  const { data: previewHydrationData } = useGetSavedLayoutByPartnerIdQuery(
    { id: partnerId, page: 1 },
    { skip: !partnerId || !useLinkedCatalog }
  );

  // Pass startDate/endDate so the BE keeps the response scoped to the active
  // window for All / Not Linked browsing. The Linked tab uses
  // listing-layout-links?includeLayout=1 instead so every linked plan appears
  // regardless of upload date.
  const { data, isFetching } = useGetSavedLayoutByPartnerIdQuery(
    { id: partnerId, page, startDate, endDate },
    { skip: !partnerId || useLinkedCatalog }
  );

  useEffect(() => {
    if (!data?.layouts) return;
    // Remember every flat layout this response surfaces so a later refetch
    // that prunes a previously-nested intermediate (e.g. cross-partner copy
    // dropped in favor of the partner's own fork) doesn't make that row
    // vanish from the tree. The tree builder consumes accumulated + ghosts.
    setSeenLayoutsFlat((prev) => {
      const next = new Map(prev);
      flattenPartnerLayoutsFromApi(data.layouts).forEach((row) => {
        if (row?._id) next.set(String(row._id), row);
      });
      return next.size === prev.size ? prev : next;
    });
    setAccumulated((prev) => {
      if (page === 1) return [...data.layouts];
      const seen = new Set(prev.map((l) => String(l._id)));
      const next = [...prev];
      data.layouts.forEach((l) => {
        if (l?._id && !seen.has(String(l._id))) {
          seen.add(String(l._id));
          next.push(l);
        }
      });
      return next;
    });
  }, [data, page]);

  const totalResults = data?.totalResults ?? accumulated.length;
  const hasMore = page * PAGE_LIMIT < totalResults && page < MAX_AUTO_PAGES;

  const hydratedLinkedCatalogRows = useMemo(() => {
    if (!useLinkedCatalog) return linkedCatalogRows;
    const hydrationByUuid = new Map();
    flattenPartnerLayoutsFromApi(previewHydrationData?.layouts || []).forEach((row) => {
      if (row?.uuid) hydrationByUuid.set(String(row.uuid), row);
    });
    flattenPartnerLayoutsFromApi(accumulated).forEach((row) => {
      if (row?.uuid) hydrationByUuid.set(String(row.uuid), row);
    });
    return linkedCatalogRows.map((linkedRow) => {
      const hydrated = linkedRow?.uuid
        ? hydrationByUuid.get(String(linkedRow.uuid))
        : null;
      return hydrated ? mergeLayoutPreviewFields(linkedRow, hydrated) : linkedRow;
    });
  }, [useLinkedCatalog, linkedCatalogRows, previewHydrationData, accumulated]);

  const flatCatalog = useMemo(() => {
    if (useLinkedCatalog) {
      return flattenPartnerLayoutsFromApi(hydratedLinkedCatalogRows);
    }
    return flattenPartnerLayoutsFromApi(accumulated);
  }, [useLinkedCatalog, hydratedLinkedCatalogRows, accumulated]);

  const feedSupplementRows = useMemo(() => {
    if (useLinkedCatalog) return [];
    return supplementCatalogFromSubmissionFeed(
      accumulated,
      submissionFeedRows,
      listingLinks
    );
  }, [useLinkedCatalog, accumulated, submissionFeedRows, listingLinks]);

  useEffect(() => {
    if (useLinkedCatalog) {
      onCatalogLoaded?.(
        mergeCatalogWithLinkedLayouts(accumulated, hydratedLinkedCatalogRows)
      );
      return;
    }
    const catalogForParent = feedSupplementRows.length
      ? mergeCatalogWithLinkedLayouts(
          [...flatCatalog, ...feedSupplementRows],
          linkedCatalogRows
        )
      : mergeCatalogWithLinkedLayouts(flatCatalog, linkedCatalogRows);
    onCatalogLoaded?.(catalogForParent);
  }, [
    useLinkedCatalog,
    flatCatalog,
    linkedCatalogRows,
    hydratedLinkedCatalogRows,
    accumulated,
    feedSupplementRows,
    onCatalogLoaded,
  ]);

  const linksByUuid = useMemo(() => layoutLinkByUuid(listingLinks), [listingLinks]);

  const matchesNode = useCallback(
    (node) => {
      const q = search.trim().toLowerCase();
      if (q && !String(node?.name || '').toLowerCase().includes(q)) return false;
      const linked = Boolean(node?.uuid && linksByUuid.has(node.uuid));
      if (filter === 'linked' && !linked) return false;
      if (filter === 'unlinked' && linked) return false;
      return true;
    },
    [search, filter, linksByUuid]
  );

  // Build the tree off accumulated + any sticky-session ghosts that the
  // current response no longer surfaces. Without this, descendants whose
  // intermediate ancestor got pruned would either orphan to the top level
  // or (with rootLayoutUuid) snap one level up — losing the in-between row
  // the user already saw nested. Flat view still derives from accumulated
  // only, so it never shows ghost rows the BE has stopped returning.
  const accumulatedWithGhosts = useMemo(() => {
    if (seenLayoutsFlat.size === 0) return accumulated;
    const presentIds = new Set(
      flattenPartnerLayoutsFromApi(accumulated)
        .map((l) => (l?._id ? String(l._id) : null))
        .filter(Boolean)
    );
    const ghosts = [];
    seenLayoutsFlat.forEach((row, id) => {
      if (!presentIds.has(id)) ghosts.push(row);
    });
    return ghosts.length ? [...accumulated, ...ghosts] : accumulated;
  }, [accumulated, seenLayoutsFlat]);

  const treeCatalogSource = useMemo(() => {
    if (useLinkedCatalog) return hydratedLinkedCatalogRows;
    if (!feedSupplementRows.length) return accumulatedWithGhosts;
    return [...accumulatedWithGhosts, ...feedSupplementRows];
  }, [useLinkedCatalog, hydratedLinkedCatalogRows, accumulatedWithGhosts, feedSupplementRows]);

  const treeRoots = useMemo(
    () => filterDisplayTreeRoots(buildPartnerLayoutTree(treeCatalogSource)),
    [treeCatalogSource]
  );

  // Roots always start collapsed. Admins reveal children via the per-row
  // "Show N copies" footer button so the section never opens as a wall of
  // nested rows — same behavior at every screen size, matching iOS Files /
  // Mail "tap to drill in" expectations. Per-search auto-expansion of
  // matching ancestors still happens via `autoExpandedIds` below.
  const defaultExpandedIds = useMemo(() => new Set(), []);

  /**
   * When a search/filter is active, every ancestor of any matching node is
   * pre-expanded so the match is reachable without manually drilling. Subtrees
   * with no matches anywhere get skipped at render time.
   */
  const { autoExpandedIds, visibleRoots } = useMemo(() => {
    const hasSearch = Boolean(search.trim());
    const hasFilter = filter !== 'all';
    if (!hasSearch && !hasFilter) {
      return { autoExpandedIds: new Set(), visibleRoots: treeRoots };
    }
    const expanded = new Set();
    const filterTree = (node) => {
      const selfMatches = matchesNode(node);
      const filteredChildren = (node.children || [])
        .map(filterTree)
        .filter(Boolean);
      if (hasSearch && filteredChildren.length > 0) expanded.add(node._id);
      if (!selfMatches && filteredChildren.length === 0) return null;
      return { ...node, children: filteredChildren };
    };
    const filtered = treeRoots.map(filterTree).filter(Boolean);
    return { autoExpandedIds: expanded, visibleRoots: filtered };
  }, [treeRoots, matchesNode, search, filter]);

  // Count of plans that have any linked listing — drives the "Linked (N)"
  // tab label so admins can see at a glance how many properties have been
  // wired up.
  const linkedPlanCount = useMemo(() => {
    if (!Array.isArray(listingLinks) || listingLinks.length === 0) return 0;
    if (hasEnrichedLinkedCatalog(listingLinks)) {
      return linkedCatalogRows.length;
    }
    return listingLinks.filter((link) => link?.layoutUuid).length;
  }, [listingLinks, linkedCatalogRows]);

  const catalogWithFeedSupplement = useMemo(
    () =>
      feedSupplementRows.length
        ? [...accumulated, ...feedSupplementRows]
        : accumulated,
    [accumulated, feedSupplementRows]
  );

  const unlinkedPlanCount = useMemo(
    () => countUnlinkedPlansInCatalog(catalogWithFeedSupplement, listingLinks) ?? 0,
    [catalogWithFeedSupplement, listingLinks]
  );

  // Index listing-summary rows by customer_ref so the merged Floor Plans /
  // Listings view (filter === 'linked') can show per-listing stats next to
  // each floor plan without re-running the listings fetch.
  const listingsLifetimeByKey = useMemo(
    () => indexListingsSummaryByLayout(listingsSummaryLifetime?.listings),
    [listingsSummaryLifetime]
  );

  const listingsPeriodByKey = useMemo(
    () => indexListingsSummaryByLayout(listingsSummaryPeriod?.listings),
    [listingsSummaryPeriod]
  );

  const displayRoots = useMemo(() => {
    if (filter !== 'linked') return visibleRoots;
    const statusFiltered = visibleRoots.filter((node) => {
      const linkRecord = node?.uuid ? linksByUuid.get(node.uuid) : null;
      return linkedRootMatchesStatusFilter(
        linkRecord,
        listingsPeriodByKey,
        linkedStatusFilter
      );
    });
    return sortLinkedPlanRoots(statusFiltered, LINKED_LISTING_SORT_KEYS.NEWEST, {
      linksByUuid,
      listingsByKey: listingsPeriodByKey,
    });
  }, [
    filter,
    visibleRoots,
    linkedStatusFilter,
    linksByUuid,
    listingsPeriodByKey,
  ]);

  const filteredCount = displayRoots.length;
  const visibleTreeRoots = useMemo(
    () => displayRoots.slice(0, visibleCount),
    [displayRoots, visibleCount]
  );

  const hasMoreForFilter = useMemo(() => {
    if (useLinkedCatalog) return false;
    return hasMore && (filter === 'all' || filter === 'unlinked');
  }, [useLinkedCatalog, hasMore, filter]);

  const canShowMoreDisplay = visibleCount < filteredCount;
  const showLoadMore =
    canShowMoreDisplay || (hasMoreForFilter && visibleCount >= filteredCount);
  const showCollapse =
    !showLoadMore && visibleCount > DISPLAY_PAGE_SIZE && visibleCount >= filteredCount;

  const handleLoadMore = useCallback(() => {
    setVisibleCount((current) => {
      if (current >= filteredCount && hasMoreForFilter && !isFetching) {
        setPage((p) => p + 1);
      }
      return current + DISPLAY_PAGE_SIZE;
    });
  }, [filteredCount, hasMoreForFilter, isFetching]);

  const handleCollapse = useCallback(() => {
    setVisibleCount(DISPLAY_PAGE_SIZE);
  }, []);

  const handleOpenPlan = useCallback((p) => {
    const url = resolveFloorGalleryUrl(p);
    if (url) window.open(url, '_blank');
  }, []);

  if (!partnerId) {
    return (
      <Box sx={{ mt: 2, p: 2, borderRadius: '14px', border: '1px solid rgba(0,0,0,0.06)' }}>
        <Typography sx={{ fontSize: '0.78rem', color: 'rgba(0,0,0,0.5)' }}>
          {partnerIdProp != null
            ? 'Could not load floor plans for this partner. Try selecting the partner again.'
            : 'Floor plan library requires a partner account.'}
        </Typography>
      </Box>
    );
  }

  const hasResults = displayRoots.length > 0;
  const linkedStatusFilterActive =
    filter === 'linked' && linkedStatusFilter !== LINKED_STATUS_FILTER_ALL;
  const linkedStatusFilterLabel = getLinkedStatusFilterLabel(linkedStatusFilter);
  const initialLoading =
    !useLinkedCatalog && page === 1 && isFetching && accumulated.length === 0;

  // Time-range label for the section subheader and empty states. Prefer the
  // human-readable `timeRangeLabel` from AnalyticsTwo.js
  // (e.g. "Last 30 days") so the section reads in the same voice as the
  // sticky date pill at the top of the page; fall back to a derived label
  // if the prop wasn't passed.
  const fallbackRangeLabel =
    timeRange === '24h' ? 'Last 24 hours' :
    timeRange === '30d' ? 'Last 30 days' :
    'Last 7 days';
  const effectiveTimeRangeLabel = timeRangeLabel || fallbackRangeLabel;

  const sectionTitle =
    filter === 'linked' ? 'Linked listings' :
    filter === 'unlinked' ? 'Floor plans — not linked' :
    'Floor plans';

  const sectionScopeCopy = (() => {
    if (filter === 'linked' || useLinkedCatalog) {
      return null;
    }
    if (filter === 'unlinked') {
      return `${unlinkedPlanCount} upload${unlinkedPlanCount !== 1 ? 's' : ''} in ${effectiveTimeRangeLabel.toLowerCase()} without a listing ID.`;
    }
    return `${unlinkedPlanCount} upload${unlinkedPlanCount !== 1 ? 's' : ''} in ${effectiveTimeRangeLabel.toLowerCase()} without a listing ID.`;
  })();

  const searchPlaceholder =
    filter === 'linked' ? 'Search linked listings' : 'Search floor plans';

  const noFloorPlansInPeriodCopy = useLinkedCatalog
    ? 'No linked floor plans yet. Link a listing ID from the Not Linked tab or use Link by URL.'
    : `No floor plans uploaded in the ${effectiveTimeRangeLabel.toLowerCase()}.`;

  return (
    <Box
      id={sectionId}
      sx={{
        mt: { xs: 1.5, sm: 2 },
        borderRadius: '14px',
        border: '1px solid rgba(0,0,0,0.06)',
        bgcolor: '#fff',
        overflow: 'hidden',
      }}
    >
      <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
        spacing={1.25}
        sx={{ px: { xs: 1.75, sm: 2 }, py: { xs: 1.25, sm: 1.5 } }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <HomeWorkOutlined sx={{ fontSize: 15, color: 'rgba(0,0,0,0.4)', flexShrink: 0 }} />
            <Typography
              sx={{
                fontSize: { xs: '0.875rem', md: '0.9375rem' },
                fontWeight: 500,
                color: 'rgba(0,0,0,0.5)',
              }}
            >
              {sectionTitle}
            </Typography>
          </Stack>
          {sectionScopeCopy && (
            <Typography
              sx={{
                display: 'block',
                mt: 0.5,
                fontSize: '0.7rem',
                color: 'rgba(0,0,0,0.42)',
                lineHeight: 1.4,
              }}
            >
              {sectionScopeCopy}
            </Typography>
          )}
        </Box>
      </Stack>

      <Stack
        direction="column"
        spacing={1}
        sx={{ px: { xs: 1.75, sm: 2 }, pb: 1.5 }}
      >
        <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%', flexShrink: 0 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <SegmentedTabs
              value={filter === 'all' ? 'unlinked' : filter}
              onChange={setFilter}
              options={[
                { value: 'linked', label: `Linked (${linkedPlanCount})` },
                { value: 'unlinked', label: 'Not Linked' },
              ]}
            />
          </Box>
        </Stack>
        <TextField
          size="small"
          placeholder={searchPlaceholder}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: 'rgba(60,60,67,0.6)' }} />
              </InputAdornment>
            ),
          }}
          // iOS-style soft "search field" — pill background, no hard outline,
          // matches the system-gray-6 fill UISearchBar uses. Identical chrome
          // on every screen size; only padding scales for touch comfort.
          sx={{
            flex: 1,
            minWidth: 0,
            '& .MuiOutlinedInput-root': {
              borderRadius: '10px',
              bgcolor: 'rgba(118,118,128,0.12)',
              fontSize: { xs: '0.9rem', sm: '0.875rem' },
              '& fieldset': { borderColor: 'transparent' },
              '&:hover fieldset': { borderColor: 'transparent' },
              '&.Mui-focused fieldset': {
                borderColor: 'rgba(0,102,255,0.55)',
                borderWidth: '1px',
              },
            },
            '& .MuiOutlinedInput-input': {
              py: { xs: 1, sm: 0.85 },
            },
          }}
        />
        {filter === 'linked' && (
          <LinkedStatusFilterPill
            value={linkedStatusFilter}
            options={linkedStatusFilterOptions}
            onChange={(next) => onLinkedStatusFilterChange?.(next)}
          />
        )}
      </Stack>

      {linkNoticeMessage && (
        <Box
          role="status"
          aria-live="polite"
          sx={{ px: { xs: 1.75, sm: 2 }, pb: 0.75 }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              px: 1.25,
              py: 0.5,
              borderRadius: '10px',
              bgcolor: linkNoticeIsError ? analyticsRedAlpha(0.06) : analyticsGreenAlpha(0.08),
              border: `1px solid ${
                linkNoticeIsError ? analyticsRedAlpha(0.18) : analyticsGreenAlpha(0.2)
              }`,
            }}
          >
            <Typography
              sx={{
                flex: 1,
                fontSize: '0.78rem',
                lineHeight: 1.3,
                color: linkNoticeIsError ? ANALYTICS_RED : ANALYTICS_GREEN,
              }}
            >
              {linkNoticeMessage}
            </Typography>
            {onDismissLinkNotice && (
              <IconButton
                size="small"
                onClick={onDismissLinkNotice}
                aria-label="Dismiss message"
                sx={{
                  flexShrink: 0,
                  width: 24,
                  height: 24,
                  color: linkNoticeIsError ? ANALYTICS_RED : ANALYTICS_GREEN,
                  '&:hover': {
                    bgcolor: linkNoticeIsError
                      ? analyticsRedAlpha(0.1)
                      : analyticsGreenAlpha(0.12),
                  },
                }}
              >
                <CloseIcon sx={{ fontSize: 14 }} />
              </IconButton>
            )}
          </Box>
        </Box>
      )}

      <Box sx={{ px: { xs: 1.75, sm: 2 }, pb: 1, minHeight: 120 }}>
        {initialLoading ? (
          <Stack spacing={1}>
            <Skeleton variant="rounded" height={64} />
            <Skeleton variant="rounded" height={64} />
          </Stack>
        ) : !hasResults ? (
          <Typography sx={{ fontSize: '0.78rem', color: 'rgba(0,0,0,0.5)', py: 2, lineHeight: 1.55 }}>
            {search.trim() || linkedStatusFilterActive || (filter !== 'all' && treeRoots.length > 0)
              ? linkedStatusFilterActive && linkedStatusFilterLabel
                ? `No linked listings with status “${linkedStatusFilterLabel}”.`
                : 'No floor plans match this filter.'
              : noFloorPlansInPeriodCopy}
          </Typography>
        ) : (
          <Box>
            {visibleTreeRoots.map((root) => (
              <FloorPlanTreeNode
                key={root._id}
                node={root}
                depth={0}
                linksByUuid={linksByUuid}
                autoExpandedIds={autoExpandedIds}
                defaultExpandedIds={defaultExpandedIds}
                layoutStatsLookup={layoutStatsLookup}
                layoutLineagePeriodLookup={layoutLineagePeriodLookup}
                layoutLineageLifetimeLookup={layoutLineageLifetimeLookup}
                listingsLifetimeByKey={filter === 'linked' ? listingsLifetimeByKey : null}
                listingsPeriodByKey={filter === 'linked' ? listingsPeriodByKey : null}
                onAttach={onAttachPlan}
                onChange={onChangePlan}
                onDisconnect={onDisconnect}
                onEditListingStatus={onEditListingStatus}
                onListingLinkSuccess={onListingLinkSuccess}
                partner={partner || partnerHostname}
                existingLinks={existingLinks || listingLinks}
                planByCustomerRef={planByCustomerRef}
                onOpen={handleOpenPlan}
                scopeLabel={effectiveTimeRangeLabel}
              />
            ))}
          </Box>
        )}
      </Box>

      {(showLoadMore || showCollapse) && (
        <Box sx={{ borderTop: '1px solid rgba(0,0,0,0.05)' }}>
          {showLoadMore && (
            <ButtonBase
              onClick={handleLoadMore}
              disabled={isFetching}
              sx={{
                width: '100%',
                py: 1.25,
                fontSize: '0.8rem',
                fontWeight: 500,
                color: 'rgba(99,102,241,0.9)',
                borderRadius: '0 !important',
                textTransform: 'none !important',
                '&:hover': { bgcolor: 'rgba(0,0,0,0.02)' },
                '&.Mui-disabled': { opacity: 0.5 },
              }}
            >
              {isFetching ? <CircularProgress size={14} sx={{ color: 'rgba(99,102,241,0.9)' }} /> : 'Load more'}
            </ButtonBase>
          )}
          {showCollapse && (
            <ButtonBase
              onClick={handleCollapse}
              sx={{
                width: '100%',
                py: 1.25,
                fontSize: '0.8rem',
                fontWeight: 500,
                color: 'rgba(99,102,241,0.9)',
                borderRadius: '0 !important',
                textTransform: 'none !important',
                '&:hover': { bgcolor: 'rgba(0,0,0,0.02)' },
              }}
            >
              Collapse
            </ButtonBase>
          )}
        </Box>
      )}
    </Box>
  );
};

export default FloorPlanLibrarySection;
